import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Actions } from '../../redux/actions';
import { UUID, Threshold } from '../../redux/models';
import { CharacteristicsIndicators } from './characteristicsIndicators.component';
import './characteristicsIndicators.style.scss';
import { getPathEdit, getWorkspacePathEdit } from './utils';
import { AppState } from '../../redux/redux.model';
import { isWorkspaceLevelMethodology } from '../../redux/methodologyDeep/scope';

const {
  deleteCharacteristicIndicatorFromMethodologyDeep,
  deleteOnlyIndicatorFromMethodologyDeep,
  toggleShowOnTimeline,
  toggleShowOnInspectionLayout
} = Actions;

export interface CharacteristicsIndicatorsProps {
  id: UUID;
  title: React.JSX.Element;
  canonical: boolean;
  timeline: boolean;
  inspectionLayout: boolean;
  indicatorId?: UUID;
  characteristicId?: UUID;
  phenomenonId: UUID;
  methodologyId: UUID;
  thresholds?: Threshold[];
  wrongConfiguration?: boolean;
}

export const CharacteristicsIndicatorsClass = ({
  id,
  indicatorId,
  characteristicId,
  title,
  canonical,
  timeline,
  inspectionLayout,
  phenomenonId,
  methodologyId,
  thresholds,
  wrongConfiguration
}: CharacteristicsIndicatorsProps) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const methodologyDeep = useSelector((state: AppState) => state.MethodologyDeep.methodologyDeep);
  const company = methodologyDeep?.company;
  const workspaceId = methodologyDeep?.workspace_id;
  const isWorkspaceMethodology = isWorkspaceLevelMethodology(methodologyDeep);

  const remove = useCallback(() => {
    dispatch(
      deleteCharacteristicIndicatorFromMethodologyDeep({
        id,
        indicatorId,
        characteristicId,
        phenomenonId
      })
    );
  }, [dispatch, id, indicatorId, characteristicId, phenomenonId]);

  const removeIndicator = useCallback(() => {
    dispatch(
      deleteOnlyIndicatorFromMethodologyDeep({
        id,
        indicatorId,
        characteristicId,
        phenomenonId
      })
    );
  }, [dispatch, id, indicatorId, characteristicId, phenomenonId]);

  const edit = useCallback(() => {
    const instanceId = characteristicId || indicatorId;
    const isIndicator = !characteristicId;
    if (isWorkspaceMethodology && workspaceId) {
      history.push(getWorkspacePathEdit(workspaceId, phenomenonId, instanceId, methodologyId, id, isIndicator));
      return;
    }
    if (!company?.id) return;
    history.push(getPathEdit(company.id, phenomenonId, instanceId, methodologyId, id, isIndicator));
  }, [history, company?.id, workspaceId, isWorkspaceMethodology, phenomenonId, characteristicId, indicatorId, methodologyId, id]);

  const toggleTimeline = () => indicatorId && dispatch(toggleShowOnTimeline(indicatorId));

  const toggleLayout = () => indicatorId && dispatch(toggleShowOnInspectionLayout(indicatorId));

  return (
    <CharacteristicsIndicators
      id={id}
      indicatorId={indicatorId}
      characteristicId={characteristicId}
      title={title}
      canonical={canonical}
      timeline={timeline}
      inspectionLayout={inspectionLayout}
      phenomenonId={phenomenonId}
      deleteFn={remove}
      deleteIndicatorFn={removeIndicator}
      editFn={edit}
      thresholds={thresholds}
      onLayoutClick={toggleLayout}
      onTimelineClick={toggleTimeline}
      wrongConfiguration={wrongConfiguration}
    />
  );
};

export default CharacteristicsIndicatorsClass;
